import * as R from "remeda";
import { putApiEmbeddingUpdateDataDescription, putApiEmbeddingUpdateDataTags, useDeleteApiEmbeddingDeleteData, useGetApiChatBotDataListByEmbeddingCollectionId, useGetApiEmbeddingCollectionGet, useGetApiWorkspaceGet } from "@/API"
import { ActionIcon, Box, Button, Group, Loader, LoadingOverlay, Pagination, Stack, Text } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useNavigate, useParams } from "@tanstack/react-router"
import { useState } from "react"
import { EmbeddingAddTextButton } from "./EmbeddingAddTextForm"
import { DataTable } from "mantine-datatable"
import { IconEdit, IconTag, IconTrash } from "@tabler/icons-react"
import { EmbeddingCollectionImportButton } from "./EmbeddingCollectionImportButton";
import { EmbeddingAddExcelButton } from "./EmbeddingAddExcelButton";
import { EmbeddingCollectionExportButton } from "./EmbeddingCollectionExportButton";
import { PageTitle } from "@/Components/PageTitle";
import { AppBreadcrumbs } from "@/Components/AppBreadcrumbs";

export function EmbeddingCollectionPage() {
    const { workspaceId, embeddingCollectionId } = useParams({ from: "/workspace/$workspaceId/embeddingCollection/$embeddingCollectionId/" })
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const workspace = useGetApiWorkspaceGet({ id: workspaceId })
    const collection = useGetApiEmbeddingCollectionGet({ workspaceId: workspaceId, id: embeddingCollectionId })
    const chatBotDataList = useGetApiChatBotDataListByEmbeddingCollectionId({ workspaceId: workspaceId, embeddingCollectionId: embeddingCollectionId })
    const PAGE_SIZE = 10
    const [activePage, setPage] = useState(1);

    const delete_Mutation = useDeleteApiEmbeddingDeleteData()
    const tagsOptions = R.pipe(collection.data?.data.embeddings ?? [], R.flatMap(x => x.tags ?? []), R.unique())
    return <Stack pos="relative" gap={5}>
        <LoadingOverlay visible={collection.isFetching} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
        <AppBreadcrumbs
            items={[
                {
                    label: '工作空間列表',
                    linkProps: { to: '/workspace' },
                },
                {
                    label: workspace.data?.data.name ?? '',
                    linkProps: { to: '/workspace/$workspaceId', params: { workspaceId } },
                },
                {
                    label: '資料集合列表',
                    linkProps: { to: '/workspace/$workspaceId/embeddingCollection', params: { workspaceId } },
                },
                {
                    label: collection.data?.data.name,
                    active: true
                }
            ]}
        />
        <PageTitle text={"資料集合"} mb={10} />

        <Group gap={5}>
            <Text>已連結的機器人設定:</Text>
            {
                chatBotDataList.isFetching ? <Loader /> :
                    !!chatBotDataList.data?.data.results?.length ?
                        chatBotDataList.data?.data.results?.map((x, idx) => <Button key={x.id ?? ""} radius="lg" variant="light" size="compact-sm" onClick={() => {
                            navigate({
                                to: "/workspace/$workspaceId/chatBotData/$chatBotDataId", params: {
                                    workspaceId: workspaceId,
                                    chatBotDataId: x.id ?? ""
                                }
                            })
                        }}>{x.name ?? ""}</Button>) : <Text>無</Text>}
        </Group>

        <Group>
            <EmbeddingAddTextButton workspaceId={workspaceId} tagsOptions={tagsOptions} collectionId={embeddingCollectionId} >
                <Button>
                    新增內容集合
                </Button>
            </EmbeddingAddTextButton>
            <EmbeddingAddExcelButton workspaceId={workspaceId} embeddingCollectionId={embeddingCollectionId}>
                上傳Excel
            </EmbeddingAddExcelButton>
        </Group>
        <Group>
            {embeddingCollectionId &&
                <>
                    <EmbeddingCollectionImportButton workspaceId={workspaceId} embeddingCollectionId={embeddingCollectionId} onSuccess={() => {
                        queryClient.invalidateQueries({ queryKey: collection.queryKey })
                        notifications.show({ message: "上傳成功" })
                    }} onError={(e) => {
                        notifications.show({ message: "上傳失敗", color: "red" })
                    }} />
                    <EmbeddingCollectionExportButton workspaceId={workspaceId} embeddingCollectionId={embeddingCollectionId} />
                </>
            }
        </Group>
        <Pagination value={activePage} onChange={setPage} total={Math.ceil((collection.data?.data.embeddings?.length ?? 0) / PAGE_SIZE)} />
        <DataTable
            withTableBorder
            minHeight={collection.data?.data.embeddings && collection.data.data.embeddings.length > 0 ? 0 : 150}
            noRecordsText="沒有資料"
            columns={[
                { accessor: 'dataId', title: "DataId" },
                { accessor: 'description', title: "簡述" },
                { accessor: 'tags', title: "標籤", render: (data) => data.tags?.join(', ') },
                {
                    accessor: 'actions',
                    title: <Box mr={6}>操作</Box>,
                    textAlign: 'right',
                    render: (data) => (
                        <Group gap={4} justify="right" wrap="nowrap">
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                onClick={() => {
                                    modals.openContextModal({
                                        modal: "TextInput",
                                        title: '修改簡述',
                                        innerProps: {
                                            initText: data.description,
                                            onConfirm: async (v) => {
                                                try {
                                                    if (data.dataId) {
                                                        await putApiEmbeddingUpdateDataDescription({
                                                            workspaceId: workspaceId,
                                                            collectionId: embeddingCollectionId,
                                                            dataId: data.dataId,
                                                            description: v
                                                        })
                                                        queryClient.invalidateQueries({ queryKey: collection.queryKey })
                                                        notifications.show({ message: "修改成功" })
                                                    }
                                                } catch (e) {
                                                    notifications.show({ message: "修改失敗", color: "red" })
                                                }
                                            },
                                        },

                                    });
                                }}
                            >
                                <IconEdit size={16} />
                            </ActionIcon>
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                onClick={() => {
                                    modals.openContextModal({
                                        modal: "TagsInput",
                                        title: '修改標籤',
                                        innerProps: {
                                            initTags: data.tags,
                                            tagsInputProps: {
                                                data: tagsOptions
                                            },
                                            onConfirm: async (v) => {
                                                try {
                                                    if (data.dataId && v) {
                                                        await putApiEmbeddingUpdateDataTags(v, {
                                                            workspaceId: workspaceId,
                                                            collectionId: embeddingCollectionId,
                                                            dataId: data.dataId,
                                                        })
                                                        queryClient.invalidateQueries({ queryKey: collection.queryKey })
                                                        notifications.show({ message: "修改成功" })
                                                    }
                                                } catch (e) {
                                                    notifications.show({ message: "修改失敗", color: "red" })
                                                }
                                            },
                                        },

                                    });
                                }}
                            >
                                <IconTag size={16} />
                            </ActionIcon>
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                color="red"
                                onClick={() => {
                                    modals.openContextModal({
                                        modal: "ConfirmCountDown",
                                        title: '刪除',
                                        innerProps: {
                                            countDownSecond: 0,
                                            children: (
                                                <Text size="sm">
                                                    是否要刪除"{data.dataId}"?
                                                </Text>
                                            ),
                                            labels: {
                                                confirm: '刪除', cancel: '取消'
                                            },
                                            confirmProps: { color: 'red' },
                                            onConfirm: async () => {
                                                try {
                                                    if (data.dataId) {
                                                        await delete_Mutation.mutateAsync({
                                                            params: {
                                                                workspaceId: workspaceId,
                                                                collectionId: embeddingCollectionId,
                                                                dataId: data.dataId
                                                            }
                                                        })
                                                        queryClient.invalidateQueries({ queryKey: collection.queryKey })
                                                        notifications.show({ message: "刪除成功" })
                                                    }
                                                } catch (e) {
                                                    notifications.show({ message: "刪除失敗", color: "red" })
                                                }
                                            },
                                        },

                                    });
                                }}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                    ),
                },
            ]}
            onRowDoubleClick={({ record }) => {
                navigate({
                    to: "/workspace/$workspaceId/embeddingCollection/$embeddingCollectionId/$dataId", params: {
                        workspaceId: workspaceId,
                        embeddingCollectionId: embeddingCollectionId,
                        dataId: record.dataId!
                    }
                })
            }}
            records={collection.data?.data.embeddings?.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE).map((x, idx) => {
                return {
                    id: x.dataId,
                    ...x
                }
            }) ?? []}
        />
    </Stack>
}